import { SearchParams } from 'framework/list';
import { CrudService, CrudServiceProviderParams, ResultResourceData } from './crud.types';
import { ResourceData, ResourceId } from './crud.types';
import type { ApiRequestOptions } from 'framework/api';

const useBaseCrudService = <
  TEntity = ResultResourceData,
  TSearchResult = ResultResourceData,
  TSearchAllResult = TEntity[],
  TFindAllResult = TEntity[],
>({
  listResourceUrl,
  singleResourceUrl,
  useService,
  entityName,
}: CrudServiceProviderParams): CrudService<TEntity, TSearchResult, TSearchAllResult, TFindAllResult> => {
  const { sendPostRequest, sendGetRequest, sendPutRequest, sendDeleteRequest } = useService();

  const create = async (data: ResourceData) => {
    const response = await sendPostRequest(listResourceUrl, data);
    const result = await response.json();
    return (result.info || result) as TEntity;
  };

  const getById = async (id: ResourceId) => {
    const url = singleResourceUrl.replace(':id', `${id}`);
    const response = await sendGetRequest(url);
    const result = await response.json();
    return (result.info || result) as TEntity;
  };

  const update = async (id: ResourceId, data: ResourceData) => {
    const url = singleResourceUrl.replace(':id', `${id}`);
    const response = await sendPutRequest(url, data);
    const result = await response.json();
    return (result.info || result) as TEntity;
  };

  const deleteById = (id: ResourceId) => {
    const url = singleResourceUrl.replace(':id', `${id}`);
    return sendDeleteRequest(url);
  };

  const search = async (request: any, options?: ApiRequestOptions) => {
    const url = `${listResourceUrl}/search`;
    const response = await sendPostRequest(url, request, options);
    return response.json() as Promise<TSearchResult>;
  };

  const searchAll = async (params: SearchParams) => {
    let totalPages = 0;
    let currentPage = 0;
    let result: any[] = [];
    const url = `${listResourceUrl}/search`;

    do {
      const response = await sendPostRequest(url, {
        pageSize: 30,
        ...params,
        pageNumber: currentPage,
      });
      const data = await response.json();

      currentPage += 1;
      totalPages = data.totalPages;
      result = [...result, ...data.result];
    } while (currentPage < totalPages);

    return result as TSearchAllResult;
  };

  const findAll = async () => {
    let totalPages = 0;
    let currentPage = 0;
    let result: any[] = [];

    do {
      const url = `${listResourceUrl}?pageNumber=${currentPage}`;
      const response = await sendGetRequest(url);
      const data = await response.json();

      currentPage += 1;
      totalPages = data.totalPages;
      result = [...result, ...data.result];
    } while (currentPage < totalPages);

    return result as TFindAllResult;
  };

  const getPermissions = async (id: ResourceId) => {
    try {
      const url = `${singleResourceUrl.replace(':id', `${id}`)}/permissions`;
      const response = await sendGetRequest(url);
      return response.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('useBaseCrudService: getPermissions: Failed to fetch permissions:', error);
      return [];
    }
  };

  const getPermissionsEntity = async (id: ResourceId) => {
    try {
      if (!entityName) {
        return [];
      }
      const response = await sendGetRequest(`/permissions/${entityName}/${id}`);
      return response.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('useBaseCrudService: getPermissions: Failed to fetch permissions:', error);
      return [];
    }
  };

  return {
    create,
    getById,
    update,
    deleteById,
    search,
    searchAll,
    getPermissions,
    getPermissionsEntity,
    findAll,
  };
};

export default useBaseCrudService;
